// Session 55 (Wave 16, D8): every client on one page.
//
// Leena: "see all the clients, maybe what they're signed up for, and what we
// think the estimated payments are ... what stage they are at, when the last
// postings happened, and make sure things don't fall apart if there are any
// errors or wait times."
//
// Reads v_portal_clients_overview (agency only). The stage is computed in the
// view from the same facts the per-client stepper uses, so the two agree. A
// red flag is anything failed, or a client step that has waited more than
// three days. Rows go to the client's stepper; everything else is a click away
// from there.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { clientPath } from '../lib/clientLink.js';
import { fmtDate, fmtDateTime } from '../components/FormBits.jsx';

const money = (cents) => '$' + (cents / 100).toFixed(cents % 100 ? 2 : 0);
const PERIOD = { monthly: 'a month', annual: 'a year', one_off: 'one-off' };
const MISSING_LABEL = {
  byline: 'author', allowlist: 'pages we may link to', money_pages: 'money pages', proof: 'proof',
  competitors: 'competitors', ga4: 'GA4', gsc: 'Search Console', approver: 'approver', description: 'what they do',
};
const STATUS_CHIP = {
  active: ['Active', 'course-chip'],
  onboarding: ['Onboarding', 'chip-run-wait'],
  archived: ['Archived', 'chip-run-muted'],
};

function pays(r) {
  if (r.amount_cents == null) return r.subscription_status ? 'Stripe, no amount' : '—';
  const per = PERIOD[r.bill_period] || (r.bill_period ? r.bill_period : '');
  return `${money(r.amount_cents)}${per ? ' ' + per : ''}`;
}

function waiting(r) {
  const bits = [];
  if (r.waiting_on_agency > 0) bits.push(`${r.waiting_on_agency} for you`);
  if (r.waiting_on_client > 0) {
    bits.push(`${r.waiting_on_client} with the client`
      + (r.next_deadline_at ? `, auto-approves ${fmtDate(r.next_deadline_at)}` : ''));
  }
  return bits.join(' · ') || '—';
}

export default function AdminClients() {
  const { isAgency, reloadClients } = useAuth();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [spend, setSpend] = useState(null);   // Session 63: this month's title-image spend

  const load = useCallback(async () => {
    const { data, error: e } = await supabase.from('v_portal_clients_overview').select('*');
    if (e) { setError(e.message); return; }
    setRows(data || []);
    // Session 63: what the image models cost this month, by client. Agency
    // only (the view is empty for anyone else); a failure here hides the card.
    const first = new Date(); first.setDate(1); first.setHours(0, 0, 0, 0);
    const { data: sp } = await supabase.from('v_portal_ai_spend')
      .select('client_id,client_name,model,cost_usd,ok').gte('at', first.toISOString());
    setSpend(sp || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  // The switcher's list and this page come from the same table; a client
  // created or archived elsewhere shows up here on the next load.
  useEffect(() => { reloadClients?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Archived clients never reach this page: the view sits behind
  // _s5_client_visible, like every other agency view.
  const sorted = useMemo(() => {
    const rank = (r) => (r.flagged ? 0 : r.status === 'onboarding' ? 1 : 2);
    return [...(rows || [])]
      .sort((a, b) => rank(a) - rank(b) || String(a.name).localeCompare(String(b.name)));
  }, [rows]);

  if (!isAgency) return <Navigate to="/" replace />;
  if (error) return <div className="empty"><h2>Couldn't load the clients</h2><p>{error}</p></div>;
  if (!rows) return <p className="lede">Loading…</p>;

  const flagged = sorted.filter((r) => r.flagged).length;

  return (
    <>
      <div className="clients-head">
        <div>
          <h1>Clients</h1>
          <p className="lede">
            {flagged === 0 ? 'Nothing is stuck.' : `${flagged} ${flagged === 1 ? 'client needs' : 'clients need'} a look.`}
            {' '}{rows.length} {rows.length === 1 ? 'client' : 'clients'}.
          </p>
        </div>
        <Link className="btn btn-primary" to="/admin/clients/new">+ Add client</Link>
      </div>

      {/* Session 63: the spend card. One line, then a chip per client. */}
      {spend && spend.length > 0 && (() => {
        const ok = spend.filter((s) => s.ok);
        const total = spend.reduce((a, s) => a + Number(s.cost_usd || 0), 0);
        const byClient = {};
        for (const s of spend) {
          const k = s.client_id;
          byClient[k] = byClient[k] || { name: s.client_name || k, n: 0, cost: 0, failed: 0 };
          if (s.ok) byClient[k].n += 1; else byClient[k].failed += 1;
          byClient[k].cost += Number(s.cost_usd || 0);
        }
        const failed = spend.length - ok.length;
        return (
          <div className="spend-card">
            <strong>Title images this month:</strong> {ok.length} {ok.length === 1 ? 'image' : 'images'}, ${total.toFixed(2)}
            {failed > 0 && <span className="chip chip-run-err" style={{ marginLeft: 8 }}>{failed} failed</span>}
            <div className="spend-chips">
              {Object.values(byClient).sort((a, b) => b.cost - a.cost).map((c) => (
                <span key={c.name} className="chip chip-run-muted">{c.name}: {c.n} for ${c.cost.toFixed(2)}</span>
              ))}
            </div>
          </div>
        );
      })()}

      <div className="clients-table-wrap">
        <table className="clients-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Plan</th>
              <th>Pays</th>
              <th>Stage</th>
              <th>Next post</th>
              <th>Last post</th>
              <th>Waiting</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => {
              const [label, chip] = STATUS_CHIP[r.status] || [r.status, 'chip-run-muted'];
              const missing = (r.pack_missing || []).map((k) => MISSING_LABEL[k] || k);
              return (
                <tr key={r.client_id} className={r.flagged ? 'row-flagged' : ''}>
                  <td>
                    <Link className="clients-name" to={clientPath(`/admin/clients/${r.client_id}/onboarding`, r.client_id)}>
                      {r.name}
                    </Link>
                    <div className="clients-sub">
                      <span className={`chip ${chip}`}>{label}</span>
                      {r.flagged && <span className="chip chip-run-err" title={r.flag_reason || ''}>{r.flag_reason || 'Needs a look'}</span>}
                    </div>
                    {missing.length > 0 && (
                      <div className="clients-missing">
                        Missing: {missing.join(', ')}.{' '}
                        <Link to={clientPath(`/admin/clients/${r.client_id}/settings?tab=edit`, r.client_id)}>Edit client</Link>
                      </div>
                    )}
                  </td>
                  <td>
                    {r.plan_names || <span className="muted">—</span>}
                    {r.cadence?.type === 'monthly' && r.cadence?.month_days && (
                      <div className="clients-sub muted">Day{r.cadence.month_days.length > 1 ? 's' : ''} {r.cadence.month_days.join(', ')} each month</div>
                    )}
                    {r.cadence && r.cadence.type !== 'monthly' && r.cadence.publish_weekdays && (
                      <div className="clients-sub muted">{r.cadence.publish_weekdays.length} a week</div>
                    )}
                  </td>
                  <td>
                    {pays(r)}
                    {r.billing_note && <div className="clients-sub muted">{r.billing_note}</div>}
                    {!r.billing_manual && r.subscription_status && <div className="clients-sub muted">Stripe: {r.subscription_status}</div>}
                  </td>
                  <td>{r.stage}</td>
                  <td>{r.next_publish_date ? fmtDate(r.next_publish_date) : <span className="muted">—</span>}</td>
                  <td>{r.last_published_date ? fmtDate(r.last_published_date) : <span className="muted">—</span>}</td>
                  <td>
                    {waiting(r)}
                    {r.failed_jobs > 0 && (
                      <div className="clients-sub">
                        {(r.failed_recent || []).map((f) => (
                          <div key={f.id} className="muted">Failed: {f.title} ({fmtDateTime(f.at)})</div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="hint">
        A client is flagged when something has failed, or when a plan, a question or a draft has
        sat with them for more than three days. Click a name for their steps; Edit client for the pack.
      </p>
    </>
  );
}
